import React from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router";
import styled, { createGlobalStyle } from "styled-components";
import ConnectPage from "./components/ConnectPage";
import GeneratePage from "./components/GeneratePage";
import WorkoutsPage from "./components/WorkoutsPage";
import WorkoutDetailPage from "./components/WorkoutDetailPage";
import BillingPage from "./components/BillingPage";
import NotFoundPage from "./components/NotFoundPage";
import { useAuthStore } from "./auth/store";

const brandLogoUrl = new URL("../public/logo.png", import.meta.url).toString();

const GlobalStyle = createGlobalStyle`
  :root {
    color-scheme: light;
  }

  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: radial-gradient(circle at 0% 0%, rgba(59, 130, 246, 0.15), transparent 45%),
      radial-gradient(circle at 100% 20%, rgba(236, 72, 153, 0.15), transparent 40%),
      #0f172a;
    color: #0f172a;
    min-height: 100vh;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
`;

const Chrome = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(12px);
  background: rgba(15, 23, 42, 0.6);
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
`;

const NavBar = styled.nav`
  max-width: 1120px;
  margin: 0 auto;
  padding: 1.1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.85rem;
    padding: 0.9rem 1rem;
  }
`;

const Brand = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: #e2e8f0;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-size: 0.95rem;
  text-decoration: none;
`;

const BrandLogo = styled.img`
  display: block;
  width: 118px;
  height: 58px;
  object-fit: cover;
  object-position: center;
`;

const DesktopNavLinks = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNavLinks = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.4rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(148, 163, 184, 0.2);
  color: #e2e8f0;
  border-radius: 12px;
  padding: 0.45rem 0.75rem;
  font-weight: 600;
  font-size: 0.86rem;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileOnlyNavLinks = styled.div<{ $open: boolean }>`
  width: 100%;
  display: none;

  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? "block" : "none")};
  }
`;

const StyledLink = styled(NavLink)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #cbd5f5;
  text-decoration: none;
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(148, 163, 184, 0.22);
    border-color: rgba(148, 163, 184, 0.35);
  }

  &.active {
    color: #0b1220;
    background: linear-gradient(135deg, #38bdf8, #818cf8);
    border-color: transparent;
    box-shadow: 0 10px 30px rgba(79, 70, 229, 0.28);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 3.5rem 1.5rem 4.5rem;

  @media (max-width: 768px) {
    padding: 1rem 0 2rem;
  }
`;

const ContentPanel = styled.div`
  width: 100%;
  max-width: 1100px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2.5rem;
`;

const PageSurface = styled.section`
  background: rgba(255, 255, 255, 0.92);
  border-radius: 28px;
  padding: 2.75rem clamp(1.5rem, 3vw, 3rem);
  box-shadow:
    0 30px 60px -20px rgba(15, 23, 42, 0.45),
    inset 0 1px 0 rgba(148, 163, 184, 0.15);
  border: 1px solid rgba(148, 163, 184, 0.18);

  @media (max-width: 768px) {
    border-radius: 0;
    border-left: none;
    border-right: none;
    padding: 1.5rem 1rem;
  }
`;

const SectionTitle = styled.h1`
  margin: 0 0 0.75rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0f172a;
`;

const SectionSubTitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #475569;
  max-width: 48ch;
`;

const PageHeader = styled.header`
  margin-bottom: 2.25rem;
`;

const App: React.FC = () => {
  const { pathname } = useLocation();
  const {
    state: { isAuthenticated, isConnected },
  } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const shouldShowNavigation = isAuthenticated;

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  if (!isAuthenticated) {
    return (
      <>
        <GlobalStyle />
        <Chrome>
          <Main>
            <ContentPanel>
              <Routes>
                <Route
                  path="/connect"
                  element={
                    <PageSurface>
                      <PageHeader>
                        <SectionTitle>Welcome to LaceAI</SectionTitle>
                        <SectionSubTitle>
                          Create an account or log in to continue to your Garmin-ready workouts.
                        </SectionSubTitle>
                      </PageHeader>
                      <ConnectPage />
                    </PageSurface>
                  }
                />
                <Route path="*" element={<Navigate to="/connect" replace />} />
              </Routes>
            </ContentPanel>
          </Main>
        </Chrome>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Chrome>
        {shouldShowNavigation ? (
          <Header>
            <NavBar>
              <Brand to="/generate" aria-label="LaceAI home"><BrandLogo src={brandLogoUrl} alt="LaceAI" /></Brand>
              <MobileMenuButton
                type="button"
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="primary-navigation"
              >
                {isMobileMenuOpen ? "Close" : "Menu"}
              </MobileMenuButton>
              <MobileOnlyNavLinks $open={isMobileMenuOpen}>
                <MobileNavLinks id="primary-navigation">
                  <StyledLink to="/connect">Account</StyledLink>
                  <StyledLink to="/generate">Generate</StyledLink>
                  <StyledLink to="/workouts">Workouts</StyledLink>
                  <StyledLink to="/billing">Billing</StyledLink>
                </MobileNavLinks>
              </MobileOnlyNavLinks>
              <DesktopNavLinks>
                <StyledLink to="/connect">Account</StyledLink>
                <StyledLink to="/generate">Generate</StyledLink>
                <StyledLink to="/workouts">Workouts</StyledLink>
                <StyledLink to="/billing">Billing</StyledLink>
              </DesktopNavLinks>
            </NavBar>
          </Header>
        ) : null}
        <Main>
          <ContentPanel>
            <Routes>
              <Route
                path="/"
                element={<Navigate to="/generate" replace />}
              />
              <Route
                path="/connect"
                element={
                  <PageSurface>
                    <PageHeader>
                      <SectionTitle>Garmin Account</SectionTitle>
                      <SectionSubTitle>
                        Link your Garmin account and review connection details used for workout sync.
                      </SectionSubTitle>
                    </PageHeader>
                    <ConnectPage />
                  </PageSurface>
                }
              />
              <Route
                path="/generate"
                element={(
                  <PageSurface>
                    <PageHeader>
                      <SectionTitle>Generate a Workout</SectionTitle>
                      <SectionSubTitle>
                        Describe your session in natural language and review the structured plan before sending it to Garmin.
                      </SectionSubTitle>
                    </PageHeader>
                    <GeneratePage />
                  </PageSurface>
                )}
              />
              <Route
                path="/workouts"
                element={isConnected ? (
                  <PageSurface>
                    <PageHeader>
                      <SectionTitle>Your Garmin Library</SectionTitle>
                      <SectionSubTitle>
                        Track recently generated workouts and drill into the details any time.
                      </SectionSubTitle>
                    </PageHeader>
                    <WorkoutsPage />
                  </PageSurface>
                ) : (
                  <Navigate to="/connect" replace />
                )}
              />
              <Route
                path="/workouts/:id"
                element={isConnected ? (
                  <PageSurface>
                    <PageHeader>
                      <SectionTitle>Workout Details</SectionTitle>
                      <SectionSubTitle>
                        Inspect the full Garmin payload so you always know what will be synced to your watch.
                      </SectionSubTitle>
                    </PageHeader>
                    <WorkoutDetailPage />
                  </PageSurface>
                ) : (
                  <Navigate to="/connect" replace />
                )}
              />
              <Route
                path="/billing"
                element={
                  <PageSurface>
                    <PageHeader>
                      <SectionTitle>Billing & Tokens</SectionTitle>
                      <SectionSubTitle>
                        Manage your Stripe subscription and monitor monthly token usage.
                      </SectionSubTitle>
                    </PageHeader>
                    <BillingPage />
                  </PageSurface>
                }
              />
              <Route
                path="*"
                element={
                  <PageSurface>
                    <PageHeader>
                      <SectionTitle>Page not found</SectionTitle>
                      <SectionSubTitle>
                        This LaceAI route does not exist.
                      </SectionSubTitle>
                    </PageHeader>
                    <NotFoundPage />
                  </PageSurface>
                }
              />
            </Routes>
          </ContentPanel>
        </Main>
      </Chrome>
    </>
  );
};

export default App;
